import { Form, message, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useForm, FormProvider } from 'react-hook-form';
import { get as _get } from 'lodash';
import React from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';

import { Account, AccountInfo } from '../../../../types/account';
import SimpleDivider from '../../global/SimpleDivider';
import FormButtonGroup from '../../global/Forms/FormButtonGroup';
import FormInput from '../../global/Forms/FormInput';
import FormSwitch from '../../global/Forms/FormSwitch';
import ChangeAccountButton from './ChangeAccountButton';
import MfaSettings from './MfaSettings';
import { booleanToInt } from '../../../utils/Utils';

type PasswordUpdate = {
  currentPassword: string;
  password: string;
  passwordConfirm: string;
};

type SettingsProps = {
  account: Account;
  updateAccountInformation: (acc: Account) => Promise<void>;
  updatePassword: (currentPw: string, oldPw: string) => Promise<any>;
  accountInfo: AccountInfo;
};

const Settings: React.FC<SettingsProps> = ({
  account,
  updateAccountInformation,
  updatePassword,
  accountInfo,
}) => {
  const { t } = useTranslation();
  const isProduction = process.env.REACT_APP_STAGE === 'production';

  const accountSchema = Yup.object({
    firstName: Yup.string().matches(/^[^0-9]+$/, t('validation:validateNoNumbers')),
    lastName: Yup.string().matches(/^[^0-9]+$/, t('validation:validateNoNumbers')),
    email: Yup.string().required(t('validation:required')).email(t('validation:invalidEmail')),
    emailNotification: Yup.number(),
  });

  const passwordSchema = Yup.object({
    currentPassword: Yup.string().required(t('validation:required')),
    password: Yup.string()
      .required(t('validation:required'))
      .min(12, t('validation:passwordLength', { length: 12 }))
      .matches(/[0-9]/, t('validation:passwordRequiresNumber'))
      .matches(/[a-z]/, t('validation:passwordRequiresLowercase'))
      .matches(/[A-Z]/, t('validation:passwordRequiresUppercase'))
      .matches(/[^\w]/, t('validation:passwordRequiresSpecial')),
    passwordConfirm: Yup.string()
      .required(t('validation:required'))
      .oneOf([Yup.ref('password')], t('validation:passwordsDontMatch')),
  });

  const accountFormMethods = useForm({
    defaultValues: {
      firstName: account.firstName || '',
      lastName: account.lastName || '',
      email: account.email || '',
      emailNotification: account.emailNotification || 0,
    },
    resolver: yupResolver(accountSchema),
    mode: 'onTouched',
  });

  const passwordFormMethods = useForm({
    defaultValues: { currentPassword: '', password: '', passwordConfirm: '' },
    resolver: yupResolver(passwordSchema),
    mode: 'onTouched',
  });

  const handleUpdateAccountInformation = async (values: Account) => {
    try {
      await updateAccountInformation(values);
      message.success(t('accountSettings:messages.settings_changed'));
      accountFormMethods.reset(values);
    } catch (error) {
      message.error(_get(error, 'response.data.message') || t('validation:unknownApiError'));
    }
  };

  const handleUpdatePassword = async (values: PasswordUpdate) => {
    const { currentPassword, password } = values;
    try {
      await updatePassword(currentPassword, password);
      message.success(t('accountSettings:messages.password_changed'));
      passwordFormMethods.reset(values);
    } catch (error) {
      message.error(_get(error, 'response.data.message') || t('validation:unknownApiError'));
    }
  };

  const accountDetails = (name: string, value: string | number | null) => (
    <div className="flex">
      <div className="text-sm ant-text-black w-32">{name}</div>
      <div className="flex-1">{value}</div>
    </div>
  );

  return (
    <div className="account overflow-auto flex-1 h-full">
      <SimpleDivider title={t('accountSettings:form.updateAccount')} />
      <FormProvider {...accountFormMethods}>
        <Form
          className="account-form"
          layout="vertical"
          onFinish={accountFormMethods.handleSubmit(handleUpdateAccountInformation)}
        >
          <FormInput name="firstName" label={t('accountSettings:form.firstName')} useHookForm />
          <FormInput name="lastName" label={t('accountSettings:form.lastName')} useHookForm />
          <FormInput
            name="email"
            type="email"
            label={t('accountSettings:form.email')}
            required
            useHookForm
          />

          <div className="inline-block">
            <span className="mr-1">{t('accountSettings:form.sendEmailNotification')}</span>
            <Tooltip title={t('accountSettings:form.emailNotificationInfoText')}>
              <InfoCircleOutlined />
            </Tooltip>
          </div>
          <div className="inline-block ml-4">
            <FormSwitch
              name="emailNotification"
              handleChange={(checked: boolean) => {
                accountFormMethods.setValue('emailNotification', booleanToInt(checked) as number, {
                  shouldDirty: true,
                });
              }}
              checkedChildren={t('common:yes')}
              unCheckedChildren={t('common:no')}
              useHookForm
            />
          </div>

          <FormButtonGroup
            disabled={!accountFormMethods.formState.isDirty}
            onSave={accountFormMethods.handleSubmit(handleUpdateAccountInformation)}
            onCancel={() => accountFormMethods.reset()}
            loading={accountFormMethods.formState.isSubmitting}
          />
        </Form>
      </FormProvider>
      <SimpleDivider title={t('accountSettings:form.updateSecurity')} />
      <FormProvider {...passwordFormMethods}>
        <Form
          className="account-form"
          layout="vertical"
          onFinish={passwordFormMethods.handleSubmit(handleUpdatePassword)}
        >
          <FormInput
            name="currentPassword"
            type="password"
            label={t('accountSettings:form.currentPassword')}
            useHookForm
          />
          <FormInput
            name="password"
            type="password"
            label={t('accountSettings:form.password')}
            useHookForm
          />
          <FormInput
            name="passwordConfirm"
            type="password"
            label={t('accountSettings:form.confirmPassword')}
            required
            useHookForm
          />
          <FormButtonGroup
            disabled={!passwordFormMethods.formState.isDirty}
            onSave={passwordFormMethods.handleSubmit(handleUpdatePassword)}
            onCancel={() => passwordFormMethods.reset()}
            loading={passwordFormMethods.formState.isSubmitting}
          />
        </Form>
      </FormProvider>

      {!isProduction && <MfaSettings />}

      <SimpleDivider title={t('accountSettings:accountDetails')} />
      {accountDetails(t('accountSettings:accountName'), accountInfo.accountName)}
      {accountDetails(
        accountInfo.isManufacturer ? t('sideBarLeft:parentId') : t('sideBarLeft:receiverId'),
        accountInfo.userId
      )}
      {accountDetails(t('sideBarLeft:accountId'), accountInfo.accountId)}
      <div className="mt-4">
        <ChangeAccountButton />
      </div>
    </div>
  );
};

export default Settings;
